import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Box,
  Button,
  Container,
  IconButton,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Link,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import T from 'i18n-react';
import { getLocale } from 'util/icwUtility';
import { HOME_PATH } from 'shared/routes';
import Header from '../Layout/Header/beforeLogin/Header';
import CompanyInfo from './components/CompanyInfo';
import ProductForm from './components/ProductForm';
import SupplierForm from './components/SupplierForm';
import LoginForm from './components/LoginForm';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '40px',
    maxWidth: '850px',
    height: '100%',
  },
  msgContainer: {
    padding: '40px',
    maxWidth: '850px',
    height: '100vh',
  },
  productThumbnail: {
    width: '96px',
    height: '96px',
    padding: '20px',
    position: 'relative',
    '& > img': {
      display: 'inline-block',
      width: '100%',
    },
  },
  removeBtn: {
    position: 'absolute',
    top: '0',
    right: '0',
  },
  formControl: {
    padding: '0 10px',
  },
  backBtn: {
    color: '#005EFF',
    backgroundColor: '#fff',
    border: '1px solid #005EFF',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  saveBtn: {
    color: '#fff',
    backgroundColor: '#005EFF',
    borderRadius: '2px',
    '&:hover': {
      backgroundColor: '#005EFF',
    },
  },
  backToDashboardBtn: {
    textTransform: 'uppercase',
  },
  clienSubtitle1: {
    display: 'block',
    '& span': {
      color: '#005EFF',
    },
  },
}));

const locale = getLocale();

const companyValidationSchema = {
  companyName: Yup.string().required(T.translate('iSource.error.companyName')),
  email: Yup.string()
    .email(T.translate('iSource.error.emailFormat'))
    .required(T.translate('iSource.error.email')),
};

const productValidationSchema = {
  productName: Yup.string().required(T.translate('iSource.error.productName')),
};

const loginValidationSchema = {
  loginEmail: Yup.string()
    .email(T.translate('iSource.error.emailFormat'))
    .required(T.translate('iSource.error.loginEmail')),
  password: Yup.string().required(T.translate('iSource.error.password')),
};

const certList = [
  { label: 'BSCI', value: 'BSCI' },
  { label: 'Sedex', value: 'Sedex' },
  { label: 'SA8000', value: 'SA8000' },
  { label: 'ISO9001', value: 'ISO9001' },
  { label: 'GMPC', value: 'GMPC' },
];

const SourceForm = ({
  isUserLogin,
  profile,
  onInit,
  onFormInit,
  onSubmit,
  iSourceStatus,
  onLogin,
  loginState,
  ...otherProps
}) => {
  const companyId = profile.id && profile.companyMaps[0].companyId;
  const classes = useStyles();
  const [files, setFiles] = useState([]);
  const [cert, setCert] = useState();
  const [country, setCountry] = useState();
  const [user, setUser] = useState('false');
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [validationSchema, setValidationSchema] = useState(
    Yup.object({
      ...productValidationSchema,
    })
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop: (acceptedFiles) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    },
  });
  const {
    enqueueSnackbar,
    // closeSnackbar
  } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      companyName: '',
      email: '',
      productName: '',
      materials: '',
      otherRequirements: '',
      loginEmail: '',
    },
    validationSchema,
    onSubmit: (values, actions) => {
      if (isUserLogin || user === 'false') {
        setReadyToSubmit(true);
      } else {
        onLogin(formik.values.loginEmail, formik.values.password);
      }
    },
  });

  const handleCertChange = (newValue) => {
    setCert(newValue);
  };

  const handleCountryChange = (newValue) => {
    setCountry(newValue);
  };

  const handleRemoveProductPhoto = (idx) => {
    const updateFile = [...files];
    updateFile.splice(idx, 1);
    setFiles(updateFile);
  };

  const handleRadioChange = (e) => {
    if (e.target.value === 'true') {
      setValidationSchema(
        Yup.object({
          ...productValidationSchema,
          ...companyValidationSchema,
          ...loginValidationSchema,
        })
      );
    } else {
      setValidationSchema(
        Yup.object({
          ...productValidationSchema,
          ...companyValidationSchema,
        })
      );
    }
    setUser(e.target.value);
  };

  const productPhoto = files.map((file, idx) => (
    <Box classes={{ root: classes.productThumbnail }} key={file.name}>
      <img src={file.preview} alt='' />
      <IconButton
        classes={{ root: classes.removeBtn }}
        onClick={() => handleRemoveProductPhoto(idx)}>
        <HighlightOffIcon />
      </IconButton>
    </Box>
  ));

  const LoginControl = () => (
    <>
      <Box display='flex' justifyContent='flex-start' py={4}>
        <Box fontSize='16px' fontWeight='bold' color='#232B3A'>
          {T.translate('iSource.form.loginTitle')}
        </Box>
      </Box>
      <Grid container spacing={2} justify='flex-start'>
        <FormControl classes={{ root: classes.formControl }} component='fieldset'>
          <RadioGroup
            aria-label='position'
            name='position'
            value={user}
            onChange={handleRadioChange}
            row>
            <FormControlLabel
              value='true'
              control={<Radio color='secondary' />}
              label={T.translate('iSource.form.account.yes')}
              labelPlacement='end'
            />
            <FormControlLabel
              value='false'
              control={<Radio color='secondary' />}
              label={T.translate('iSource.form.account.no')}
              labelPlacement='end'
            />
          </RadioGroup>
        </FormControl>
      </Grid>
    </>
  );

  const SuccessMsg = () => (
    <>
      <Box display='flex' justifyContent='center' fontSize='28px' fontWeight='bold' py={2}>
        {T.translate('iSource.success.title')}
      </Box>
      <Box display='flex' justifyContent='center' fontSize='18px' fontWeight='nomal' py={2}>
        {T.translate('iSource.success.walkIn.subtitle')}
      </Box>
      <Box display='flex' justifyContent='center' py={4}>
        <Link href={`http://${window.location.host}/${locale}/iSource`}>
          <Box fontSize='14px' fontWeight='500' color='#005EFF'>
            {T.translate('iSource.success.walkIn.back')}
          </Box>
        </Link>
      </Box>
    </>
  );

  const SuccessMsgUser = () => (
    <>
      <Box
        display='flex'
        justifyContent='center'
        align='center'
        fontSize='28px'
        fontWeight='bold'
        py={2}>
        {T.translate('iSource.success.title')}
      </Box>
      <Box
        display='flex'
        justifyContent='center'
        align='center'
        fontSize='18px'
        fontWeight='nomal'
        py={2}
        className={classes.clienSubtitle1}>
        {T.translate('iSource.success.client.subtitle1', { seconds: <span>30</span> })}
      </Box>
      <Box
        display='flex'
        justifyContent='center'
        align='center'
        fontSize='18px'
        fontWeight='nomal'
        py={2}>
        {T.translate('iSource.success.client.subtitle2')}
      </Box>
      <Box display='flex' justifyContent='center' align='center' py={4}>
        <Link href={`http://${window.location.host}${HOME_PATH}`}>
          <Box
            fontSize='14px'
            fontWeight='500'
            color='#005EFF'
            className={classes.backToDashboardBtn}>
            {T.translate('iSource.success.client.back')}
          </Box>
        </Link>
      </Box>
    </>
  );

  useEffect(() => {
    onFormInit();
    if (!isUserLogin) {
      onInit();
      setValidationSchema(
        Yup.object({
          ...productValidationSchema,
          ...companyValidationSchema,
        })
      );
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (readyToSubmit) {
      const formData = {
        ...formik.values,
        certification: cert && cert.value,
        location: country && country.value,
      };
      const filesDz = {
        files,
        path: companyId ? `/${companyId}/iSource` : `/iSource/walkIn`,
      };

      onSubmit(filesDz, formData, companyId);
    }

    // eslint-disable-next-line
  }, [readyToSubmit]);

  useEffect(() => {
    if (loginState.status === 'failed') {
      enqueueSnackbar(T.translate(`login.errors.${loginState.failReason}`), {
        variant: 'warning',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
      onInit();
    } else if (loginState.status === 'success') {
      setReadyToSubmit(true);
    }

    // eslint-disable-next-line
  }, [loginState.status]);

  return (
    <div className={classes.root}>
      {!isUserLogin && <Header {...otherProps} />}
      <Box bgcolor='#FFF' height={isUserLogin ? '100vh' : '100%'}>
        {iSourceStatus !== 'success' ? (
          <Container classes={{ maxWidthMd: classes.container }} component='div' maxWidth='md'>
            <Box display='flex' justifyContent='flex-start' py={2}>
              <Box fontSize='28px' fontWeight='bold' color='#232B3A'>
                {T.translate('iSource.form.title')}
              </Box>
            </Box>
            {!isUserLogin && <CompanyInfo {...formik} />}
            <ProductForm
              {...formik}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              productPhoto={productPhoto}
            />
            <SupplierForm
              cert={cert}
              certList={certList}
              country={country}
              handleCertChange={handleCertChange}
              handleCountryChange={handleCountryChange}
            />

            {!isUserLogin && <LoginControl />}
            {user === 'true' && <LoginForm {...formik} />}
            <Box display='flex' justifyContent='space-between' my={4}>
              <Button classes={{ root: classes.backBtn }} variant='contained' color='primary'>
                {T.translate('iSource.form.back')}
              </Button>
              <Button
                classes={{ root: classes.saveBtn }}
                variant='contained'
                color='primary'
                onClick={(values, actions) => {
                  formik.handleSubmit(values, actions);
                }}
                disabled={
                  formik.isSubmitting &&
                  loginState.status !== 'initialized' &&
                  loginState.status !== 'failed'
                }>
                {T.translate('iSource.form.postRequest')}
              </Button>
            </Box>
          </Container>
        ) : (
          <Container classes={{ maxWidthMd: classes.msgContainer }} component='div' maxWidth='md'>
            <Box mt='30%'>{isUserLogin ? <SuccessMsgUser /> : <SuccessMsg />}</Box>
          </Container>
        )}
      </Box>
    </div>
  );
};

SourceForm.propTypes = {
  classes: PropTypes.object,
  profile: PropTypes.object,
  isUserLogin: PropTypes.bool.isRequired,
  onInit: PropTypes.func,
  onFormInit: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default SourceForm;
